/* HOW TO USE

this mixin can use as the base of autocomplete from server site
below methods need to implement in the subcomponent
1. dataToDescAndId -- return the object { sId: xxx, sDesc: xxx }, sId use as ID and sDesc used to show title in select box
2. callApi -- provide the api get information
3. if using props: isShareCache, you need to create 'name' attr in your component and also add field in store[commonComponent]

emit
1. value -- Array of String
2. data -- Array of Object

<template>
  <!-- don't cache-items, it will cause the emitChange method cannot find the staff object to emit -->
  <v-autocomplete
    ref="autocomplete"
    class="search-staff"
    :value="value"
    :items="items"
    :loading="isLoading"
    :search-input.sync="search"
    hide-no-data
    item-text="sDesc"
    item-value="sId"
    :label="label"
    :placeholder="placeholder"
    :outlined="outlined"
    :clearable="clearable"
    :hint="hint"
    :persistent-hint="persistentHint"
    @change="emitChange($event)"
    @input="onInput"
  >
    <template v-slot:selection="{item}">
      <div
        v-if="isShowSelection"
        style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; pointer-events: none; width: calc(99% - 1px);"
      >
        {{ staffToString(item) }}
      </div>
    </template>
  </v-autocomplete>
</template>

*/

import _ from 'lodash'
import { sleep } from '@/utils/asynUtil'
import store from '@/store'
import { mapState } from 'vuex'
import { commonComponentModule } from '@/store/moduleName'
import commonComponent from '@/store/modules/common/components'
// import g2eApi from '../api/hr/g2e'
// import constant from '@/constant/constant'
// import axios from 'axios'
// import { isFieldBlank } from '../utils/stringUtil'

if (!store.state[commonComponentModule]) store.registerModule(commonComponentModule, commonComponent)

const autocompleteMixin = {
  props: {
    label: {
      type: String,
      default: null
    },
    placeholder: {
      type: String,
      default: null
    },
    outlined: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: undefined
    },
    filled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: null
    },
    persistentHint: {
      type: Boolean,
      default: null
    },
    value: { // have emit
      type: Array,
      default: function () {
        return []
      }
    },
    isShareCache: { // component don't call search api if other component already have the value
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: function () {
        return []
      }
    }
  },
  data: function () {
    return {
      dataCacheFromServer: [], // this will cache the data that get from server
      dataCacheForSelected: [], // this will cache the selected data
      isLoading: false,
      search: null // String: searching key word
    }
  },

  // sId
  // sDesc

  computed: {
    localValue: {
      get: function () {
        return [...this.value]
      }
      // set will no trigger when autocomplete remove item...
    },
    items () { // need to combie with dataSelected
      let tData = [...this.dataCacheFromServer]
      if (this.isShareCache && this.storeValue) {
        tData = [...tData, ...this.storeValue]
      }
      tData = [...tData, ...this.dataCacheForSelected]
      let tDataWithDescAndId = tData.map(data => {
        return Object.assign({}, data, this.dataToDescAndId(data))
      })
      return _.uniqBy([...tDataWithDescAndId], 'sId')
    },
    ...mapState(commonComponentModule, {
      storeValue: function (state) {
        return state.autocompleteMixinShareCache[this.$options.name]
      },
      storeIsLoading: function (state) { return state.autocompleteMixinIsLoading[this.$options.name] }
    })
  },
  watch: {
    search: function (val) {
      // Call backend to search
      // if it don't have search keyword, don't search
      if (val === null || val === '') return
      this.isLoading = true
      this.debounceGetData(val)
    },
    value: function (val) { // if data update outside, update dataSelected
      this.onValueChange(val)
    }
  },
  created: function () {
    this.debounceGetData = _.debounce(this.getData, 500)
  },
  mounted: async function () {
    // To support default value
    // prefer a search in order to update the list
    if (this.localValue !== null && this.localValue.length) {
      await this.shareCacheWait(this.value)
      this.onValueChange(this.value)
    }
  },
  methods: {
    shareCacheWait: async function (temp) {
      if (this.isShareCache) {
        if (this.storeIsLoading === true) {
          await sleep(300)
          await this.shareCacheWait(temp)
        } else {
          this.setStoreIsLoading(true) // only run this if is shareCahe in store
        }
      }
    },
    onValueChange: async function (values) {
      // To support update value from component outside
      // only if the values not in the result list, perform search
      if (values === null || values.length === 0) return
      // in case group id, change it to string
      let isIdInItemsArray = []

      // In order to improve performance, using muti async call at the same time. Promise all to way them all
      let asycValuesFromServer = [] // promise list
      for (let index = 0; index < values.length; index++) {
        let val = values[index]
        isIdInItemsArray[index] = false
        this.items.forEach(item => {
          if (item.sId === val) {
            isIdInItemsArray[index] = true
          }
        })

        // should only happens when the value is set by program outside
        if (!isIdInItemsArray[index]) {
          // this.search = val
          this.isLoading = true
          asycValuesFromServer.push(this.getDataFromServer(val, true))
        }
      }

      // if (this.isShareCache && this.storeValue && this.dataCacheForSelected === null) {
      //   this.dataCacheForSelected = this.storeValue
      // }

      let tValues = await Promise.all(asycValuesFromServer)

      if (tValues && tValues.length) {
        this.dataCacheForSelected = this.dataCacheForSelected.concat(tValues.flat())
      }

      if (!isIdInItemsArray.every(val => val === true)) {
        this.emitChange(values)
      }

      this.setStoreIsLoading(false) // only run this if is shareCahe in store
    },
    dataToDescAndId: function (data) { // need to implement in children component
      return { sId: null, sDesc: null }
    },
    getData: async function (val, isPropUpdated = false) {
      this.dataCacheFromServer = await this.getDataFromServer(val, isPropUpdated)
    },
    getDataFromServer: async function (val, isPropUpdated = false) { // if it call server because prop is updated, isPropUpdated = true
      let data = []
      let cb = response => {
        if (response.length === 0) {
          data = []
        } else {
          data = response
        }
      }
      let finalCb = () => {
        this.isLoading = false
      }
      await this.callApi(cb, finalCb, val, isPropUpdated)
      return data
    },
    callApi: function (cb, finalCb, val, isPropUpdated) { // need to implement in children component
      return null
    },
    emitChange: function (event) {
      this.search = null
      let dataForEmit = []
      event.forEach(val => {
        let data = this.items.filter(item => item.sId === val)[0]
        if (data) {
          dataForEmit.push(data)
        }
      })

      // update component local cache when emit change
      this.dataCacheForSelected = [...dataForEmit]

      this.setStoreCache(dataForEmit) // only run this if is shareCahe in store

      this.$emit('input', event)
      this.$emit('dataChange', dataForEmit)
    },
    remove: function (data) {
      // remove item from value array
      let index = this.localValue.indexOf(data.sId)
      if (index >= 0) this.localValue.splice(index, 1)

      // remove item from dataCacheForSelected array
      let indexDataCacheForSelected = _.findIndex(this.dataCacheForSelected, ['sId', data.sId])
      if (indexDataCacheForSelected >= 0) this.dataCacheForSelected.splice(indexDataCacheForSelected, 1)

      // Remove from array don't trigger vue change detection, so trigger emit by myself here
      this.emitChange(this.localValue)
    },
    setStoreCache: function (value) {
      if (!this.isShareCache) return
      let name = this.$options.name
      this.$store.commit(commonComponentModule + '/setAutocompleteMixinShareCache', {
        key: name,
        value: value
      })
    },
    setStoreIsLoading: function (isLoading) {
      if (!this.isShareCache) return
      let name = this.$options.name
      this.$store.commit(commonComponentModule + '/setAutocompleteMixinIsLoading', {
        key: name,
        value: isLoading
      })
    }
  }
}

export default autocompleteMixin
